// WorkspaceSwitcher.tsx
// Stateless helper. Consumer fetches/owns workspace data; this component
// renders the dropdown + popover and emits onChange.

import { useState } from 'react';
import { Dropdown, Button, Tooltip, Typography, theme as antTheme } from 'antd';
import { Building2, Check, ChevronDown } from 'lucide-react';
import type { MenuProps } from 'antd';
import type { WorkspaceSwitcherProps, WorkspaceSwitcherWorkspace } from './types';
import { TRANSITION } from './constants';

const { Text } = Typography;

function subtitle(ws: WorkspaceSwitcherWorkspace): string | null {
  const parts = [ws.plan, ws.role].filter((p): p is string => !!p);
  return parts.length > 0 ? parts.join(' · ') : null;
}

function WorkspaceAvatar({ size = 28, color }: { size?: number; color: string }) {
  return (
    <span
      style={{
        width: size,
        height: size,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color,
        flexShrink: 0,
      }}
    >
      <Building2 size={Math.round(size * 0.55)} strokeWidth={1.75} />
    </span>
  );
}

export function WorkspaceSwitcher({
  workspaces,
  currentId,
  onChange,
  collapsed = false,
}: WorkspaceSwitcherProps) {
  // Track open state so we can rotate the chevron to signal open/closed.
  const [open, setOpen] = useState(false);
  const { token } = antTheme.useToken();
  const current = workspaces.find((w) => w.id === currentId) ?? workspaces[0];
  const buttonLabel = `Switch workspace, current: ${current?.name ?? 'none'}`;

  const workspaceItems: MenuProps['items'] = workspaces.map((w) => {
    const sub = subtitle(w);
    return {
      key: w.id,
      // Empty title suppresses AntD's auto-derived hover tooltip (which
      // would duplicate the label content as a right-anchored popup).
      title: '',
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 220 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {w.name}
            </div>
            {sub && (
              <div
                style={{
                  fontSize: 11,
                  color: token.colorTextTertiary,
                  marginTop: 2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {sub}
              </div>
            )}
          </div>
          {w.id === currentId && <Check size={14} color="#52c41a" />}
        </div>
      ),
      onClick: () => onChange(w.id),
    };
  });

  const menuItems: MenuProps['items'] = [
    {
      type: 'group',
      label: 'Organizations',
      children: workspaceItems,
    },
  ];

  // Single rendered structure across collapsed/expanded: the outlined
  // button is always block-width and the workspace name + chevron animate
  // their max-width / opacity / margin in lockstep with the sider's
  // collapse — same pattern as Brand.tsx — so nothing snaps on toggle.
  // 8px top/bottom + 12px horizontal keeps the switcher visually close
  // to both the brand above and the nav menu below.
  const outerPadding = '8px 12px';
  // In collapsed mode the workspace name isn't visible, so add a tooltip
  // showing it. Expanded mode shows the name inline, so no tooltip.
  const tooltipTitle = collapsed ? current?.name ?? '' : '';
  return (
    <div style={{ padding: outerPadding }}>
      <Dropdown
        menu={{ items: menuItems }}
        placement="bottomLeft"
        trigger={['click']}
        open={open}
        onOpenChange={setOpen}
        overlayClassName="br-appshell-workspace-switcher-popover"
      >
        <Tooltip title={tooltipTitle} placement="right">
        <Button
          type="text"
          block
          aria-label={buttonLabel}
          className="br-appshell-workspace-switcher-trigger"
          style={{
            background: 'transparent',
            border: `1px solid ${token.colorBorder}`,
            color: token.colorText,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            height: 40,
            // paddingLeft 9 aligns the avatar with the nav menu icons
            // (outer 12 + border 1 + paddingLeft 9 = sider-x 22).
            // paddingRight 13 keeps the chevron-down on the same rail as
            // the parent SubMenu chevrons in expanded mode.
            paddingLeft: 9,
            paddingRight: 13,
            borderRadius: 6,
            overflow: 'hidden',
            minWidth: 0,
          }}
        >
          <WorkspaceAvatar size={28} color={token.colorText} />
          <span
            style={{
              flex: 1,
              minWidth: 0,
              // Gap to text reduced (8 → 4) so the org name lines up with
              // the nav menu item labels below — AntD inline items put
              // ~10px between icon (x=24, right edge 40) and label, so
              // labels start near x=50. With the avatar 28 wide at sider-x
              // 18 (right edge 46), 4px gap puts the name at x=50.
              marginLeft: collapsed ? 0 : 4,
              maxWidth: collapsed ? 0 : 200,
              opacity: collapsed ? 0 : 1,
              overflow: 'hidden',
              transition: `max-width ${TRANSITION}, opacity ${TRANSITION}, margin-left ${TRANSITION}`,
            }}
          >
            <Text
              style={{
                color: token.colorText,
                fontSize: 13,
                fontWeight: 500,
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textAlign: 'left',
              }}
            >
              {current?.name ?? '—'}
            </Text>
          </span>
          <span
            aria-hidden="true"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              flexShrink: 0,
              marginLeft: collapsed ? 0 : 6,
              maxWidth: collapsed ? 0 : 14,
              opacity: collapsed ? 0 : 1,
              overflow: 'hidden',
              transition: `max-width ${TRANSITION}, opacity ${TRANSITION}, margin-left ${TRANSITION}`,
            }}
          >
            <ChevronDown
              size={14}
              color={token.colorTextTertiary}
              style={{
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.15s ease',
              }}
            />
          </span>
        </Button>
        </Tooltip>
      </Dropdown>
    </div>
  );
}
